import { useCallback, useEffect, useRef, useState } from "react";
import { wsUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Manages the lifecycle of a Casual 1v1 match WebSocket.
 *
 * Reconnect semantics (per spec):
 *   1. Heartbeat ping every 15s keeps proxies/idle-timeouts from silently dropping the socket.
 *   2. If the socket closes WHILE the player is in an active match (found/round/result),
 *      we do NOT immediately show "Disconnected". Instead we:
 *        a. Flip into a `reconnecting` phase with a 6-second countdown.
 *        b. Auto-attempt to reconnect — opening a fresh WS and re-authenticating.
 *           The backend's `reconnect_player` will re-attach us to the live match
 *           and resend the current round (with resumed:true).
 *        c. If we reconnect in time, we transparently restore game state.
 *        d. Only when the grace window expires AND we're still offline do we
 *           surface the end / forfeit screen.
 *   3. If the close happens BEFORE a match was active (still queueing), we
 *      just fall through to phase=end with reason=connection_lost.
 */
const RECONNECT_GRACE_S = 6;       // must match backend MatchManager grace
const RECONNECT_RETRY_MS = 1500;   // how often to retry the WS during grace
const ACTIVE_PHASES = new Set(["round", "result", "found"]);

const HANDLERS = {
  authed: (m, s, send, ctx) => {
    // On reconnect the backend may immediately push a `round` payload via
    // reconnect_player. If we were already reconnecting, don't re-queue.
    if (ctx.wasReconnecting) {
      // wait for the reconnect_player to either send a round (live match) or
      // we'll get a queued state.
      return;
    }
    s.setPhase("queue");
    send({ action: "queue", filters: ctx.filtersRef.current || {} });
  },
  queued:        (m, s) => s.setPhase("queue"),
  match_found:   (m, s) => { s.setOpponent(m.opponent); s.setPhase("found"); },
  round:         (m, s, _send, ctx) => {
    s.setRound(m);
    s.setScores(m.scores);
    s.setResult(null);
    s.setTimeLeft(m.timer || 120);
    s.setSkipState({ votes: m.skip_votes || 0, required: m.skip_required || 2, mine: false });
    s.setPhase("round");
    if (m.resumed || ctx.wasReconnecting) {
      // We successfully reattached — clear reconnect state
      s.setReconnect({ active: false, secondsLeft: 0 });
      ctx.markReconnected();
      toast.success("Reconnected — back in the match", { duration: 2200 });
    }
  },
  round_result: (m, s) => {
    s.setResult(m);
    if (m.scores) s.setScores(m.scores);
    s.setSkipState({ votes: 0, required: 2, mine: false });
    s.setPhase("result");
  },
  match_end: (m, s, _send, ctx) => {
    s.setEndData(m);
    s.setPhase("end");
    s.setReconnect({ active: false, secondsLeft: 0 });
    ctx.endedRef.current = true;
    ctx.onMatchEnd?.();
  },
  answer_wrong: (m, s) => { toast.error(m.msg || "Incorrect, try again", { duration: 1800 }); s.bumpWrong?.(); },
  skip_update: (m, s) => s.setSkipState((prev) => ({ votes: m.votes, required: m.required, mine: prev.mine })),
  error:     (m)    => toast.error(m.msg || "Server error"),
};

export function useMatchSocket({ enabled, token, filters }) {
  const { refresh } = useAuth();
  const onMatchEndRef = useRef(refresh);
  onMatchEndRef.current = refresh;

  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectCountdownRef = useRef(null);
  const reconnectRetryRef = useRef(null);
  const reconnectDeadlineRef = useRef(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const unmountedRef = useRef(false);
  const endedRef = useRef(false);

  const [phase, setPhase] = useState("connecting");
  const [opponent, setOpponent] = useState(null);
  const [round, setRound] = useState(null);
  const [scores, setScores] = useState([{ username: "You", score: 0 }, { username: "---", score: 0 }]);
  const [timeLeft, setTimeLeft] = useState(120);
  const [result, setResult] = useState(null);
  const [endData, setEndData] = useState(null);
  const [skipState, setSkipState] = useState({ votes: 0, required: 2, mine: false });
  const [reconnect, setReconnect] = useState({ active: false, secondsLeft: 0 });
  const [wrongPulse, setWrongPulse] = useState(0);

  // Phase tracker outside of React state to read inside onclose without stale closure
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const setters = {
    setPhase, setOpponent, setRound, setScores, setTimeLeft,
    setResult, setEndData, setSkipState, setReconnect,
    bumpWrong: () => setWrongPulse((n) => n + 1),
  };

  const stopReconnect = useCallback(() => {
    if (reconnectCountdownRef.current) { clearInterval(reconnectCountdownRef.current); reconnectCountdownRef.current = null; }
    if (reconnectRetryRef.current) { clearTimeout(reconnectRetryRef.current); reconnectRetryRef.current = null; }
  }, []);

  const openSocket = useCallback((isReconnect = false) => {
    if (unmountedRef.current || endedRef.current) return;
    let ws;
    try { ws = new WebSocket(wsUrl()); } catch (e) { /* unreachable host */ return; }
    wsRef.current = ws;
    const ctx = {
      filtersRef,
      wasReconnecting: isReconnect,
      markReconnected: () => { stopReconnect(); },
      endedRef,
      onMatchEnd: () => onMatchEndRef.current?.(),
    };

    ws.onopen = () => {
      try { ws.send(JSON.stringify({ action: "auth", token })); } catch (_e) { /* */ }
      if (pingRef.current) clearInterval(pingRef.current);
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ action: "ping" })); } catch { /* ignore */ }
        }
      }, 15000);
    };

    ws.onmessage = (ev) => {
      let m;
      try { m = JSON.parse(ev.data); } catch (parseErr) { console.error("[ws] bad payload", parseErr); return; }
      const handler = HANDLERS[m.type];
      if (handler) handler(m, setters, (p) => { try { ws.send(JSON.stringify(p)); } catch (_) { /* */ } }, ctx);
    };

    ws.onerror = (err) => { console.error("[ws] connection error", err); };

    ws.onclose = () => {
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (unmountedRef.current || endedRef.current) return;
      const p = phaseRef.current;
      // We were in an active match — start the reconnect dance instead of
      // showing the defeat / disconnect screen immediately.
      if (ACTIVE_PHASES.has(p) || p === "reconnecting") {
        beginReconnect();
      } else {
        // Wasn't in a match — fail straight through.
        setEndData((prev) => prev || {
          winner: null, reason: "connection_lost", forfeit: true, scores: [],
        });
        setPhase("end");
      }
    };
  }, [token, stopReconnect]);

  const beginReconnect = useCallback(() => {
    if (unmountedRef.current || endedRef.current) return;
    // Already reconnecting? leave countdown running.
    if (phaseRef.current === "reconnecting") return;

    setPhase("reconnecting");
    reconnectDeadlineRef.current = Date.now() + RECONNECT_GRACE_S * 1000;
    setReconnect({ active: true, secondsLeft: RECONNECT_GRACE_S });

    // tick the countdown every 500ms
    if (reconnectCountdownRef.current) clearInterval(reconnectCountdownRef.current);
    reconnectCountdownRef.current = setInterval(() => {
      const ms = reconnectDeadlineRef.current - Date.now();
      const s = Math.max(0, Math.ceil(ms / 1000));
      setReconnect({ active: true, secondsLeft: s });
      if (ms <= 0) {
        // Grace window expired and we're still offline → fail the match.
        stopReconnect();
        if (endedRef.current || unmountedRef.current) return;
        setReconnect({ active: false, secondsLeft: 0 });
        setEndData((prev) => prev || {
          winner: null, reason: "connection_lost", forfeit: true, scores: [],
        });
        setPhase("end");
        toast.error("Could not reconnect — match ended");
      }
    }, 500);

    // schedule retries — keep trying until either a) reconnected (handled by
    // round/authed handler) or b) deadline expires.
    const retry = () => {
      if (unmountedRef.current || endedRef.current) return;
      if (Date.now() >= reconnectDeadlineRef.current) return;
      if (phaseRef.current !== "reconnecting") return; // restored
      openSocket(true);
      // schedule another retry in case this one also closes
      reconnectRetryRef.current = setTimeout(retry, RECONNECT_RETRY_MS);
    };
    // small initial backoff so the close handler completes
    reconnectRetryRef.current = setTimeout(retry, 600);
  }, [openSocket, stopReconnect]);

  // single connect-on-mount lifecycle (deps intentionally exclude `filters` —
  // we read latest filters from filtersRef so changes don't kill the socket)
  useEffect(() => {
    if (!enabled || !token) return undefined;
    unmountedRef.current = false;
    endedRef.current = false;
    openSocket(false);
    return () => {
      unmountedRef.current = true;
      stopReconnect();
      if (pingRef.current) clearInterval(pingRef.current);
      try { wsRef.current?.close(); } catch (closeErr) { console.error("[ws] close failed", closeErr); }
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token]);

  // round countdown timer (pauses during reconnect — server resyncs on reconnect_player)
  useEffect(() => {
    clearInterval(timerRef.current);
    if (phase === "round") {
      timerRef.current = setInterval(() => setTimeLeft((t) => Math.max(0, t - 1)), 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [phase, round]);

  const send = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify(payload)); } catch (_) { /* */ }
  };

  const sendAnswer = (text, extras = {}) => {
    if (!text || phase !== "round") return;
    send({ action: "answer", answer: text, ...extras });
  };

  const sendSkip = () => {
    if (phase !== "round") return;
    setSkipState((prev) => ({ ...prev, mine: true }));
    send({ action: "skip" });
  };

  // Anti-cheat 1v1: forfeit just the current round (NOT the whole match).
  // Backend awards the round point to the opponent and moves on.
  const sendForfeitRound = (reason) => {
    if (phase !== "round") return;
    send({ action: "forfeit_round", reason: reason || "tab_blur" });
  };

  // Casual 1v1 explicit forfeit — instantly awards victory to the opponent.
  const sendLeaveMatch = () => {
    send({ action: "leave_match" });
    // Optimistically end so guard doesn't re-prompt. Backend will broadcast
    // match_end to the opponent.
    endedRef.current = true;
    stopReconnect();
    setReconnect({ active: false, secondsLeft: 0 });
  };

  return { phase, opponent, round, scores, timeLeft, result, endData,
           skipState, reconnect, wrongPulse, sendAnswer, sendSkip, sendLeaveMatch,
           sendForfeitRound };
}
